import * as amplitude from '@amplitude/analytics-browser'
import type { AmplitudeUserProperties, AmplitudeState } from '@/types/amplitude'

class AmplitudeService {
  private state: AmplitudeState = {
    isInitialized: false,
    isEnabled: import.meta.env.VITE_ENABLE_AMPLITUDE === 'true',
  }

  /**
   * Initialize Amplitude Analytics
   * @param config - Optional configuration overrides
   */
  async initialize(config?: amplitude.Types.BrowserOptions): Promise<void> {
    // Check if Amplitude is enabled
    if (!this.state.isEnabled) {
      console.log('Amplitude analytics is disabled')
      return
    }

    // Check if already initialized
    if (this.state.isInitialized) {
      console.warn('Amplitude is already initialized')
      return
    }

    const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY

    if (!apiKey || apiKey === 'your-amplitude-api-key-here') {
      console.warn('Amplitude API key is not configured. Analytics will not be initialized.')
      return
    }

    try {
      // Initialize Amplitude with default configuration
      const defaultConfig: amplitude.Types.BrowserOptions = {
        flushQueueSize: 30,
        flushIntervalMillis: 10000,
        logLevel: import.meta.env.DEV
          ? amplitude.Types.LogLevel.Verbose
          : amplitude.Types.LogLevel.Warn,
        // Add more default configurations as needed
      }

      // Merge default config with provided config
      const finalConfig = { ...defaultConfig, ...config }

      await amplitude.init(apiKey, undefined, finalConfig)

      this.state.isInitialized = true
      console.log('Amplitude initialized successfully')
    } catch (error) {
      console.error('Failed to initialize Amplitude:', error)
      throw error
    }
  }

  /**
   * Identify a user and set their properties
   * @param userId - The user's unique identifier
   * @param userProperties - Optional user properties to set
   */
  identify(userId: string, userProperties?: AmplitudeUserProperties): void {
    if (!this.isActive()) return

    try {
      // Set the user ID
      amplitude.setUserId(userId)

      // Set user properties if provided
      if (userProperties) {
        const identify = new amplitude.Identify()

        Object.entries(userProperties).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            identify.set(key, value)
          }
        })

        amplitude.identify(identify)
      }

      console.log(`Amplitude: User ${userId} identified`)
    } catch (error) {
      console.error('Failed to identify user in Amplitude:', error)
    }
  }

  /**
   * Update user properties
   * @param userProperties - User properties to update
   */
  setUserProperties(userProperties: AmplitudeUserProperties): void {
    if (!this.isActive()) return

    try {
      const identify = new amplitude.Identify()

      Object.entries(userProperties).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          identify.set(key, value)
        }
      })

      amplitude.identify(identify)
      console.log('Amplitude: User properties updated')
    } catch (error) {
      console.error('Failed to set user properties in Amplitude:', error)
    }
  }

  /**
   * Reset the current user (typically called on logout)
   */
  reset(): void {
    if (!this.isActive()) return

    try {
      amplitude.reset()
      console.log('Amplitude: User reset')
    } catch (error) {
      console.error('Failed to reset Amplitude user:', error)
    }
  }

  /**
   * Track an event
   * @param eventName - The name of the event
   * @param eventProperties - Optional event properties
   */
  track(eventName: string, eventProperties?: Record<string, any>): void {
    if (!this.isActive()) return

    try {
      amplitude.track(eventName, eventProperties)
      console.log(`Amplitude: Event tracked - ${eventName}`, eventProperties)
    } catch (error) {
      console.error('Failed to track event in Amplitude:', error)
    }
  }

  /**
   * Check if Amplitude is active (enabled and initialized)
   */
  private isActive(): boolean {
    if (!this.state.isEnabled) {
      return false
    }

    if (!this.state.isInitialized) {
      console.warn('Amplitude is not initialized. Call initialize() first.')
      return false
    }

    return true
  }

  /**
   * Get the current state of Amplitude
   */
  getState(): AmplitudeState {
    return { ...this.state }
  }
}

// Export a singleton instance
export const amplitudeService = new AmplitudeService()

// Export the class for testing purposes
export { AmplitudeService }
